import { createContext, useContext, useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { supabase } from '../lib/supabase'
import {
  ASK_SESSION_DEFAULT_TURNS,
  EMPTY_ASK_SESSION,
  clearAskSession,
  readAskSession,
  writeAskSession,
  type AskSession,
} from '../lib/askOneSession'

/**
 * THE ASK ONE CONVERSATION, ONCE, ABOVE THE ROUTES (1 Nov, HLP-13's session
 * half; lib/askOneSession.ts has the why).
 *
 * Every route renders its own AppShell, so anything the shell holds is reset
 * by navigating - which is how a link in One's answer lost the conversation.
 * This provider is mounted ONCE in App.tsx's signed-in branch, above
 * <Routes>, so the conversation, the draft, the log ref, the chip and whether
 * the panel is open survive a route change; it is mirrored to sessionStorage
 * so they survive a reload too. The drawer reads and writes it; AppShell reads
 * `open`.
 *
 * Outside the provider (a screen rendered on its own, the suite) the hook
 * falls back to local state - the pre-1 Nov behaviour - so nothing breaks
 * for want of a wrapper.
 */

type AskOneSessionValue = {
  session: AskSession
  setSession: Dispatch<SetStateAction<AskSession>>
  /** Start over: empty, and forgotten by the tab. */
  reset: () => void
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  /** A question is with One. Here so a navigation mid-answer keeps the wait. */
  asking: boolean
  setAsking: Dispatch<SetStateAction<boolean>>
}

const Ctx = createContext<AskOneSessionValue | null>(null)

export function AskOneSessionProvider({ owner, children }: { owner: string; children: ReactNode }) {
  const [session, setSession] = useState<AskSession>(() => readAskSession(owner) ?? EMPTY_ASK_SESSION)
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  /** ask-one sends at most this many exchanges (086); keeping more is noise. */
  const [turns, setTurns] = useState(ASK_SESSION_DEFAULT_TURNS)

  useEffect(() => {
    let live = true
    void supabase.rpc('ask_one_limits').then(
      ({ data }) => {
        const row = (Array.isArray(data) ? data[0] : data) as { history_turns?: unknown } | null
        if (live && typeof row?.history_turns === 'number' && row.history_turns > 0) setTurns(row.history_turns)
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])

  useEffect(() => writeAskSession(owner, session, turns), [owner, session, turns])

  const value = useMemo<AskOneSessionValue>(
    () => ({
      session,
      setSession,
      reset: () => {
        setSession(EMPTY_ASK_SESSION)
        clearAskSession()
      },
      open,
      setOpen,
      asking,
      setAsking,
    }),
    [session, open, asking],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** The conversation, from the provider; local state where there is none. */
export function useAskOneSession(): AskOneSessionValue {
  const shared = useContext(Ctx)
  const [session, setSession] = useState<AskSession>(EMPTY_ASK_SESSION)
  const [open, setOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  if (shared) return shared
  return { session, setSession, reset: () => setSession(EMPTY_ASK_SESSION), open, setOpen, asking, setAsking }
}
